import React from 'react'
import styled, { css } from 'styled-components'

import { Typography, TypographyProps } from '../Typography'
import { Icon, IconType } from '../Icon'

const TagDefault = styled.span<Partial<TagProps>>`
  gap: 5px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: background-color 0.3s ease;
  background-color: var(--surface-primary);
  white-space: nowrap;
  height: 36px;

  ${(props) =>
    props.clickable &&
    css`
      cursor: pointer;
    `}

  ${(props) =>
    !props.inline &&
    css`
      border-radius: var(--border-radius-xs);
      border: 1px solid var(--border);
      padding: 8px 15px;

      ${!!props.icon && `padding: 0 14px 0 10px;`}
    `}

  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      opacity: 0.6;
    `}

  ${(props) =>
    props.size === 'medium' &&
    css`
      padding: 5px 7px;
      height: 30px;

      * {
        font-size: 0.85rem;
      }
    `}
    
    ${(props) =>
      props.size === 'small' &&
      css`
      padding: 3px 5px;
      height: 24px;

      * {
        font-size: 0.65rem;
      }
    `}

  ${(props) =>
    props.variant === 'gray' &&
    css`
      background: var(--gray200);

      * {
        color: var(--text-primary);
      }
    `}

  ${(props) =>
    props.variant === 'green' &&
    css`
      border-color: var(--success);
      color: var(--success);
    `}

  ${(props) =>
    props.variant === 'red' &&
    css`
      border-color: var(--danger);
      color: var(--danger);
    `}

  ${(props) =>
    props.variant === 'tag-primary' &&
    css`
      border-color: var(--border);
      background-color: var(--indigo100);

      &:hover {
        background: var(--indigo300);
      }

      * {
        color: var(--text-primary);
      }
    `}

    
  ${(props) =>
    props.backgroundColor &&
    css`
      background-color: ${props.backgroundColor};
    `}

  ${(props) =>
    props.borderColor &&
    css`
      border-color: ${props.borderColor};
    `}

  ${(props) =>
    props.color &&
    css`
      color: ${props.color};
    `}

  ${(props) =>
    props.variant === 'success' &&
    css`
      background-color: var(--green100);
      border-color: var(--green100);
    `}

  ${(props) =>
    props.variant === 'danger' &&
    css`
      background-color: var(--red100);
      border-color: var(--red100);
    `}

  ${(props) =>
    props.variant === 'warning' &&
    css`
      background-color: var(--orange100);
      border-color: var(--orange100);
    `}  

  ${(props) =>
    props.variant === 'info' &&
    css`
      background-color: var(--blue100);
      border-color: var(--blue100);
    `}


    ${(props) =>
      props.hoverClose &&
      css`
      .tag-close-icon {
        display: none;
      }

      &:hover {
        .tag-close-icon {
          display: block;
        }
      }
    `}
`

type TagVariant =
  | 'default'
  | 'gray'
  | 'green'
  | 'red'
  | 'tag-primary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'

export type TagProps = {
  children?: React.ReactNode
  icon?: IconType
  inline?: boolean
  clickable?: boolean
  labelTextVariant?: TypographyProps['variant']
  role?: string
  disabled?: boolean
  noWrapper?: boolean
  size?: 'small' | 'medium'
  hideTitle?: boolean
  variant?: TagVariant
  color?: string
  backgroundColor?: string
  borderColor?: string
  hoverClose?: boolean
  onClose?: () => void
}

const Tag = ({
  labelTextVariant = 'number-sm',
  ...props
}: TagProps & React.ComponentPropsWithoutRef<'span'>) => {
  return (
    <TagDefault {...props}>
      {props.icon && (
        <Icon
          height={20}
          width={20}
          type={props.icon}
          hideTitle={props.hideTitle}
        />
      )}
      {props.noWrapper ? (
        props.children
      ) : (
        <Typography variant={labelTextVariant}>{props.children}</Typography>
      )}
      {props.onClose && (
        <Icon
          height={20}
          width={20}
          type='CLOSE_X'
          className='tag-close-icon'
          hideTitle={props.hideTitle}
          onClick={props.onClose}
          style={{ cursor: 'pointer' }}
        />
      )}
    </TagDefault>
  )
}

export default Tag
